import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import MetricAPI from "../../../api/metric"
import { OpportunityDashboardContainer } from "./Atom"

import OpportunityDashboardHeader from "../../components/Headers/OpportunityDashboardHeader"
import OpportunityDashboardTable from "../../components/Table/OpportunityDashboardTable"

import { getTokenLocal } from "../../../helper/utils"
import { setLoading, setOpenSidebar } from "../../redux/slices/appSlice"
import { useResponseHandle } from "../../hooks"
import { useNavigate } from "react-router-dom"
import {
  setOpportunityAdvisors,
  setOpportunityGroups,
  setOpportunityRegions,
  setOpportunityPractices,
  setOpportunityStatuses,
  setOpportunityTableData,
  appendOpportunityTableData,
  setOpportunityPagination,
  resetOpportunityTableData,
} from "../../redux/slices/opportunitySlice"

const metricAPI = new MetricAPI()
export default function MetricDashboard() {
  const token: any = getTokenLocal()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  dispatch(setOpenSidebar(false))

  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const opportunitySelectedAdvisor = useSelector((state: any) => {
    return state.opportunity.opportunitySelectedAdvisors?.[0]
  })
  const opportunitySelectedGroup = useSelector((state: any) => {
    return state.metric.maevaSelectedGroupsNoAll?.[0]
  })
  const opportunitySelectedRegion = useSelector((state: any) => {
    return state.opportunity.opportunitySelectedRegions?.[0]
  })
  const opportunitySelectedPractice = useSelector((state: any) => {
    return state.opportunity.opportunitySelectedPractices?.[0]
  })
  const opportunitySelectedStatus = useSelector((state: any) => {
    return state.opportunity.opportunitySelectedStatuses?.[0]
  })
  const opportunityPagination = useSelector((state: any) => {
    return state.opportunity.opportunityPagination
  })

  const fetchAdvisorsByGroup = async () => {
    const advisors: Array<Object> = [{ label: "All", uid: "all", name: "all" }]

    const response = await metricAPI.getAdvisorsByGroup(
      token,
      opportunitySelectedGroup?.group_sk || "all",
    )

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        result?.data?.forEach((ad: any) => {
          advisors.push({
            ...ad,
            label: `${ad.first_name} ${ad.last_name}`,
            name: `${ad.first_name} ${ad.last_name}`,
          })
        })

        dispatch(setOpportunityAdvisors(advisors))
      }
    }

    return result
  }

  const fetchGroupsByAdvisor = async () => {
    const groups: Array<Object> = [
      { label: "All", group_sk: "all", name: "all" },
    ]

    const response = await metricAPI.getGroupsByAdvisor(
      token,
      opportunitySelectedAdvisor?.uid || "all",
    )

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        result?.data?.forEach((g: any) => {
          groups.push({ ...g, label: g.group_name, name: g.group_name })
        })
        dispatch(setOpportunityGroups(groups))
      }
    }

    return result
  }

  const fetchRegions = async () => {
    const regions: Array<Object> = [
      { label: "All", region: "all", name: "all" },
    ]

    const response = await metricAPI.getMetricRegionsByGroups(token, [
      opportunitySelectedGroup?.group_sk || "all",
    ])

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        response?.data?.data?.regions?.forEach((region: any) => {
          regions.push({ label: region, region: region.toLowerCase() })
        })

        dispatch(setOpportunityRegions(regions))
      }
    }

    return result
  }

  const fetchPractices = async () => {
    const practice: Array<Object> = [
      { label: "All", practice_sk: "all", name: "all" },
    ]

    const response = await metricAPI.getMetricPracticesByRegions(
      token,
      [opportunitySelectedGroup?.group_sk],
      [opportunitySelectedRegion?.region],
    )

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        response?.data?.data?.map((ele: any) => {
          practice.push({ ...ele, label: ele.name, name: ele.name })
        })

        dispatch(setOpportunityPractices(practice))
      }
    } else {
      navigate("/auth")
    }

    return result
  }

  const fetchStatuses = async () => {
    const statuses: Array<Object> = [
      { label: "All", status: "all", name: "all" },
    ]

    const response = await metricAPI.getOpportunityStatus(token)

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        result?.data?.forEach((stat: any) => {
          statuses.push({
            ...stat,
            label: stat.status.replace(/^./, stat.status[0].toUpperCase()),
            name: stat.status,
          })
        })
        dispatch(setOpportunityStatuses(statuses))
      }
    }

    return result
  }

  const fetchOpportunityTable = async (
    page: number = 1,
    isLoadMore: boolean = false,
  ) => {
    if (isLoadMore) {
      setIsLoadingMore(true)
    } else {
      dispatch(setLoading(true))
    }

    const response = await metricAPI.getOpportunityDashboard(
      token,
      opportunitySelectedAdvisor?.uid || "all",
      opportunitySelectedGroup?.group_sk || "all",
      opportunitySelectedRegion?.region || "all",
      opportunitySelectedPractice?.practice_sk || "all",
      opportunitySelectedStatus?.status || "all",
      page,
      opportunityPagination.limit,
    )

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        // Handle response data - backend returns data and pagination separately
        // After useResponseHandle, result.data contains the nested object with data & pagination
        let responseData
        let paginationData

        // Check if result.data has the nested structure with pagination
        if (result?.data?.pagination) {
          // Backend implemented pagination correctly
          responseData = result.data.data
          paginationData = result.data.pagination
          console.log("✅ Backend returned pagination:", paginationData)
          console.log("Data items:", responseData?.length)
        } else if (Array.isArray(result?.data)) {
          // Fallback: Old structure - data is directly an array
          console.warn(
            "⚠️ Backend hasn't implemented pagination - using all data",
          )
          responseData = result.data

          // Create mock pagination for single page (all data at once)
          const dataLength = responseData.length
          paginationData = {
            page: 1,
            limit: dataLength,
            total: dataLength,
            totalPages: 1,
          }
          console.log("Created fallback pagination:", paginationData)
        } else {
          // Unexpected structure
          console.error("Unexpected API response structure:", result.data)
          responseData = []
          paginationData = {
            page: 1,
            limit: 0,
            total: 0,
            totalPages: 1,
          }
        }

        if (isLoadMore) {
          // Append data for load more
          dispatch(appendOpportunityTableData(responseData))
        } else {
          // Reset and set data for initial load
          dispatch(setOpportunityTableData(responseData))
        }

        // Update pagination info
        if (paginationData) {
          dispatch(setOpportunityPagination(paginationData))
        }
      }
    }

    if (isLoadMore) {
      setIsLoadingMore(false)
    } else {
      dispatch(setLoading(false))
    }
  }

  const loadMoreOpportunities = async () => {
    // Check if we have more pages to load
    if (
      opportunityPagination.page < opportunityPagination.totalPages &&
      !isLoadingMore
    ) {
      const nextPage = opportunityPagination.page + 1
      console.log("📄 Loading page:", nextPage)
      await fetchOpportunityTable(nextPage, true)
    }
  }

  useEffect(() => {
    fetchGroupsByAdvisor()
    fetchAdvisorsByGroup()
    fetchStatuses()
  }, [])

  useEffect(() => {
    if (opportunitySelectedAdvisor?.uid) {
      fetchGroupsByAdvisor()
    }
  }, [opportunitySelectedAdvisor])

  useEffect(() => {
    if (opportunitySelectedGroup?.group_sk) {
      fetchAdvisorsByGroup()
      fetchRegions()
    }
  }, [opportunitySelectedGroup])

  useEffect(() => {
    if (
      opportunitySelectedGroup?.group_sk &&
      opportunitySelectedRegion?.region
    ) {
      fetchPractices()
    }
  }, [opportunitySelectedRegion])

  useEffect(() => {
    // Reset pagination and fetch first page when filters change
    dispatch(resetOpportunityTableData())
    fetchOpportunityTable(1, false)
  }, [
    opportunitySelectedAdvisor,
    opportunitySelectedGroup,
    opportunitySelectedRegion,
    opportunitySelectedPractice,
    opportunitySelectedStatus,
  ])

  return (
    <OpportunityDashboardContainer>
      <OpportunityDashboardHeader name="Opportunity" />
      <OpportunityDashboardTable
        fetchOpportunityTable={fetchOpportunityTable}
        loadMoreOpportunities={loadMoreOpportunities}
        isLoadingMore={isLoadingMore}
      />
    </OpportunityDashboardContainer>
  )
}
